<template>
	<header class="fixed inset-x-0 top-0 z-50 w-full transition-all duration-300" :class="headerThemeClass">
		<!-- Main nav row -->
		<ResponsiveWrapper>
			<nav class="flex h-20 w-full items-center justify-between">
				<a href="/" class="inline-flex items-center" aria-label="Burse Home">
					<img
						v-if="useLightTheme"
						:src="burseLogoColor"
						alt="Burse logo"
						class="h-9 w-auto transition-opacity duration-300"
					/>
					<img
						v-else
						:src="burseLogoWhite"
						alt="Burse logo"
						class="logo-white h-9 w-auto transition-opacity duration-300"
					/>
				</a>

				<!-- Desktop nav -->
				<div
					class="hidden items-center gap-4 md:flex"
				>
					<ul class="flex items-center gap-1 text-sm font-medium" :class="navTextClass">
						<li @mouseenter="showProductsMenu = true" @mouseleave="showProductsMenu = false">
							<button
								class="flex items-center gap-2 rounded-full px-3 py-2.5 transition-opacity duration-200 hover:opacity-70"
							>
								Products
								<svg
									width="16"
									height="16"
									viewBox="0 0 16 16"
									fill="none"
									class="transition-transform duration-300"
									:class="showProductsMenu ? 'rotate-180' : ''"
								>
									<path d="M4 6l4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
								</svg>
							</button>
						</li>
						<li>
							<a href="/about-us" class="block rounded-full px-3 py-2.5 transition-opacity duration-200 hover:opacity-70">
								About Us
							</a>
						</li>
						<li>
							<a href="/burse/calculator" class="block rounded-full px-3 py-2.5 transition-opacity duration-200 hover:opacity-70">
								Wealth Calculator
							</a>
						</li>
					</ul>

					<wnp-button
						variants="primary"
						size="medium"
						class="w-40"
						@click="router.push('/waitlist')"
					>
						Join waitlist
					</wnp-button>
				</div>

				<!-- Mobile hamburger -->
				<button
					type="button"
					class="inline-flex h-11 w-11 items-center justify-center rounded-full md:hidden"
					:class="menuBtnClass"
					aria-label="Open menu"
					@click="isMenuOpen = !isMenuOpen"
				>
					<span class="relative block h-3.5 w-5">
						<span class="absolute left-0 top-0 h-0.5 w-full rounded-full" :class="hamburgerLineClass" />
						<span class="absolute left-0 top-1/2 h-0.5 w-full -translate-y-1/2 rounded-full" :class="hamburgerLineClass" />
						<span class="absolute bottom-0 left-0 h-0.5 w-full rounded-full" :class="hamburgerLineClass" />
					</span>
				</button>
			</nav>
		</ResponsiveWrapper>

		<!-- Products sub-nav strip (opens on hover, desktop only) -->
		<transition name="subnav-fade">
			<div
				v-if="showProductsMenu"
				class="hidden w-full bg-white/95 backdrop-blur-md md:block"
				@mouseenter="showProductsMenu = true"
				@mouseleave="showProductsMenu = false"
			>
				<ResponsiveWrapper>
					<div class="flex items-center gap-3 py-2.5">
						<a
							v-for="product in products"
							:key="product.label"
							:href="product.href"
							class="flex-1 rounded-full px-6 py-2.5 text-sm text-wnpGrey transition-colors duration-200 hover:bg-secondary-new"
							:class="isActiveProduct(product.href) ? 'bg-secondary-new' : ''"
							@click="showProductsMenu = false"
						>
							{{ product.label }}
						</a>
					</div>
				</ResponsiveWrapper>
			</div>
		</transition>

		<!-- Mobile full-screen overlay -->
		<teleport to="body">
			<transition name="mobile-menu">
				<div
					v-if="isMenuOpen"
					class="fixed inset-0 z-[60] flex flex-col bg-white md:hidden"
				>
					<!-- Header row -->
					<div class="flex items-center justify-between px-5 py-4">
						<a href="/" aria-label="Burse Home" @click="isMenuOpen = false">
							<img :src="burseLogoColor" alt="Burse" class="h-10 w-auto" />
						</a>
						<button
							type="button"
							class="flex h-9 w-9 items-center justify-center rounded-full"
							aria-label="Close menu"
							@click="isMenuOpen = false"
						>
							<svg width="20" height="20" viewBox="0 0 20 20" fill="none">
								<path d="M5 5l10 10M15 5L5 15" stroke="#EA4E4B" stroke-width="1.8" stroke-linecap="round"/>
							</svg>
						</button>
					</div>

					<!-- Nav items -->
					<div class="flex-1 overflow-y-auto py-5">
						<div class="flex flex-col gap-3 px-0">

							<!-- Products (expandable) -->
							<button
								type="button"
								class="flex w-full items-center justify-between px-5 py-2.5"
								@click="isProductsOpen = !isProductsOpen"
							>
								<span class="text-18 font-medium text-dark-840">Products</span>
								<svg
									width="18" height="18" viewBox="0 0 18 18" fill="none"
									class="transition-transform duration-300"
									:class="isProductsOpen ? 'rotate-180' : ''"
								>
									<path d="M4.5 6.75L9 11.25l4.5-4.5" stroke="#474747" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
								</svg>
							</button>

							<!-- Products sub-items -->
							<transition name="products-expand">
								<div v-if="isProductsOpen" class="mx-5 flex flex-col gap-0 rounded-3xl bg-greyBg px-6 py-4">
									<a
										v-for="product in products"
										:key="product.label"
										:href="product.href"
										class="block py-2.5 text-base font-normal text-dark-840"
										@click="isMenuOpen = false; isProductsOpen = false"
									>
										{{ product.label }}
									</a>
								</div>
							</transition>

							<a href="/about-us" class="block px-5 py-2.5 text-18 font-medium text-dark-840" @click="isMenuOpen = false">About Us</a>
							<a href="/burse/calculator" class="block px-5 py-2.5 text-18 font-medium text-dark-840" @click="isMenuOpen = false">Wealth Calculator</a>
						</div>
					</div>

					<!-- Bottom CTA -->
					<div class="px-5 py-6">
						<button
							class="w-full rounded-full bg-secondary py-3.5 text-14 font-medium tracking-wide text-white transition-colors hover:bg-secondary-dark"
							@click="openAppModalFromMenu"
						>
							Join waitlist
						</button>
					</div>
				</div>
			</transition>
		</teleport>

		<AppDownloadModal v-model="showAppDownloadModal" />
	</header>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import burseLogoWhite from "../assets/burse-logo.svg?url";
import burseLogoColor from "../assets/burse-logo-color.svg?url";
import ResponsiveWrapper from "./ResponsiveWrapper.vue";
import WnpButton from "./ui/WnpButton.vue";
import AppDownloadModal from "./AppDownloadModal.vue";

const props = defineProps({
	transparentAtTop: {
		type: Boolean,
		default: true,
	},
	topUsesLightTheme: {
		type: Boolean,
		default: false,
	},
	scrollThreshold: {
		type: Number,
		default: 24,
	},
	autoThemeOnScroll: {
		type: Boolean,
		default: true,
	},
});

const route = useRoute();
const router = useRouter();

const products = [
	{ label: "Build Wealth", href: "/burse/buildwealth" },
	{ label: "Burse Vault", href: "/burse/bursevault" },
	{ label: "Burse Flex", href: "/burse/burseflex" },
	// Burse Collective disabled — nav entry intentionally removed, page kept in codebase
];

const isMenuOpen = ref(false);
const isProductsOpen = ref(false);
const isScrolled = ref(false);
const showAppDownloadModal = ref(false);
const showProductsMenu = ref(false);

const isActiveProduct = (href) => {
	if (href === "#") return false;
	return route.path.startsWith(href);
};

const useLightTheme = computed(() => {
	if (!props.transparentAtTop) return true;
	if (showProductsMenu.value) return true;
	if (isScrolled.value && props.autoThemeOnScroll) return true;
	return props.topUsesLightTheme;
});

const headerThemeClass = computed(() => {
	const isTransparent = props.transparentAtTop && (!props.autoThemeOnScroll || !isScrolled.value) && !showProductsMenu.value;
	return isTransparent
		? "bg-transparent"
		: "bg-white/95 shadow-md backdrop-blur-md";
});

const navTextClass = computed(() => {
	return useLightTheme.value ? "text-dark-900" : "text-white";
});

const menuBtnClass = computed(() => {
	return useLightTheme.value ? "bg-black/5" : "bg-white/10";
});

const hamburgerLineClass = computed(() => {
	return useLightTheme.value ? "bg-dark-900" : "bg-white";
});

const mobileMenuClass = computed(() => {
	return useLightTheme.value
		? "border-black/10 bg-white"
		: "border-white/20 bg-black/40 backdrop-blur-md";
});

const handleScroll = () => {
	isScrolled.value = window.scrollY > props.scrollThreshold;
};

const openAppModalFromMenu = () => {
	isMenuOpen.value = false;
	isProductsOpen.value = false;
	router.push('/waitlist');
};

onMounted(() => {
	handleScroll();
	window.addEventListener("scroll", handleScroll, { passive: true });
});

onUnmounted(() => {
	window.removeEventListener("scroll", handleScroll);
});
</script>

<style scoped>
.logo-white {
	filter: drop-shadow(0 1px 1px rgba(0, 0, 0, 0.25));
}

.mobile-menu-enter-active,
.mobile-menu-leave-active {
	transition: opacity 0.25s ease, transform 0.25s ease;
}

.mobile-menu-enter-from,
.mobile-menu-leave-to {
	opacity: 0;
	transform: translateX(100%);
}

.products-expand-enter-active,
.products-expand-leave-active {
	transition: opacity 0.2s ease, max-height 0.25s ease;
	max-height: 300px;
	overflow: hidden;
}

.products-expand-enter-from,
.products-expand-leave-to {
	opacity: 0;
	max-height: 0;
}

.subnav-fade-enter-active,
.subnav-fade-leave-active {
	transition: opacity 0.2s ease, transform 0.2s ease;
}

.subnav-fade-enter-from,
.subnav-fade-leave-to {
	opacity: 0;
	transform: translateY(-4px);
}
</style>
